import { Newspaper, Trophy, Search, Menu } from 'lucide-react';
import { useState, useRef, useCallback, useEffect } from 'react';
import Globe3D from '@/components/Globe3D';

const TABS = [
  { id: 'news', label: 'News', icon: Newspaper },
  { id: 'rankings', label: 'Rankings', icon: Trophy },
  { id: 'stage', label: '', isCenter: true },
  { id: 'explore', label: 'Explore', icon: Search },
  { id: 'more', label: 'More', icon: Menu },
];

export default function BottomTabBar({ activeTab, onTabChange }) {
  const [globeSpinning, setGlobeSpinning] = useState(false);
  const [spinSpeed, setSpinSpeed] = useState(0);
  const pressTimer = useRef(null);
  const startTime = useRef(0);

  // 2026-07 UX audit P2 — cancel any in-flight rAF loop on unmount.
  useEffect(() => () => cancelAnimationFrame(pressTimer.current), []);

  const handleGlobeDown = useCallback((e) => {
    e.preventDefault();
    startTime.current = Date.now();
    setGlobeSpinning(true);
    // Tiny haptic chirp on touch — feels good on mobile, no-op on
    // desktops/browsers that don't support it. The constant tap-to-spin
    // never touches the backend (see switchTab idempotency); this is
    // purely a UX flourish.
    try { if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(15); } catch { /* ignore */ }

    const accelerate = () => {
      const elapsed = Date.now() - startTime.current;
      const progress = Math.min(elapsed / 2000, 1); // 2 seconds to max
      // 2026-07 UX audit P1 — quantize to 0.5 steps so React re-renders
      // ~18 times over the 2s hold instead of at 60fps (main-thread jank).
      const next = Math.round((1 + progress * 9) * 2) / 2; // 1 to 10
      setSpinSpeed((prev) => (prev === next ? prev : next));
      if (progress < 1) {
        pressTimer.current = requestAnimationFrame(accelerate);
      }
    };
    pressTimer.current = requestAnimationFrame(accelerate);
  }, []);

  const handleGlobeUp = useCallback(() => {
    cancelAnimationFrame(pressTimer.current);
    setGlobeSpinning(false);
    setSpinSpeed(0);
    // Soft "settle" haptic so the user feels the release. Optional.
    try { if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(8); } catch { /* ignore */ }
    onTabChange('stage');
  }, [onTabChange]);

  const handleGlobeLeave = useCallback(() => {
    // Stop spinning but do NOT navigate — navigation only on release
    cancelAnimationFrame(pressTimer.current);
    setGlobeSpinning(false);
    setSpinSpeed(0);
  }, []);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 px-3 pb-[max(env(safe-area-inset-bottom),12px)] pt-2 glass-bg border-t border-border/60"
      data-testid="bottom-tab-bar"
    >
      <div className="flex items-end justify-around max-w-md mx-auto">
        {TABS.map(({ id, label, icon: Icon, isCenter }) => {
          const isActive = activeTab === id;

          if (isCenter) {
            return (
              <div
                key={id}
                className="relative -mt-6 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                role="button"
                tabIndex={0}
                aria-label="Open Stage"
                aria-current={isActive ? 'page' : undefined}
                onMouseDown={handleGlobeDown}
                onMouseUp={handleGlobeUp}
                onMouseLeave={handleGlobeLeave}
                onTouchStart={handleGlobeDown}
                onTouchEnd={handleGlobeUp}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTabChange('stage'); } }}
                style={{ touchAction: 'none' }}
                data-testid="bottom-tab-stage"
              >
                <div 
                  className="h-14 w-14 rounded-full overflow-hidden shadow-[0_12px_30px_-12px_hsl(var(--ring)/0.55)] ring-1 ring-border/40"
                  style={{
                    transform: globeSpinning ? `scale(${1 + spinSpeed * 0.02})` : 'scale(1)',
                    transition: globeSpinning ? 'none' : 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  }}
                >
                  <Globe3D size={56} spinning={globeSpinning} spinSpeed={spinSpeed} brightness={0.85} />
                </div>
                {/* Glow effect that intensifies with spin */}
                <div 
                  className="absolute inset-0 rounded-full pointer-events-none"
                  style={{
                    boxShadow: globeSpinning 
                      ? `0 0 ${12 + spinSpeed * 3}px rgba(74,144,217,${0.3 + spinSpeed * 0.05})` 
                      : '0 0 8px rgba(74,144,217,0.2)',
                    transition: globeSpinning ? 'none' : 'box-shadow 0.6s ease-out',
                  }}
                />
              </div>
            );
          }

          return (
            <button
              key={id}
              onClick={() => onTabChange(id)}
              aria-current={isActive ? 'page' : undefined}
              className="flex flex-col items-center justify-center gap-1 py-1 px-2 min-w-[48px] active:scale-[0.98] transition-transform"
              data-testid={`bottom-tab-${id}`}
            >
              <Icon
                className={`h-5 w-5 transition-colors ${isActive ? 'text-primary dark:text-primary' : 'text-muted-foreground'}`}
                strokeWidth={isActive ? 2.5 : 1.5}
                fill="none"
                style={isActive ? { opacity: 1 } : { opacity: 0.6 }}
              />
              <span className={`text-[10px] transition-colors ${isActive ? 'text-primary font-semibold dark:text-primary' : 'text-muted-foreground'}`}>
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
